import { parseDecimalInput } from '../../../shared/numeric/localeNumeric';
import { normalizeCsvNumericFieldFromExcel } from '../../../shared/csv-core/domain/excelCsvNumeric';
import {
  parseCsvBoolean,
  parseCsvItemType,
} from '../../domain/csv/csvParseHelpers';
import {
  masterCandidateIdsEqual,
  resolveMasterFromRecordFields,
  type MasterMatchCandidate,
} from '../../domain/csv/masterMatching';
import {
  normalizeProductCode,
  sanitizeBarcodeCsvCell,
  validateBarcode,
} from '../../domain/catalogValidation';

type ParsedRow = Record<string, string>;

export type ProductCsvExistingSnapshot = {
  id: string;
  name: string;
  price: number;
  sku: string | null;
  barcode: string | null;
  categoryId: string | null;
  itemType: string;
  isActive: number;
  brandId: string | null;
  internalNotes: string | null;
  preferredSupplierId: string | null;
  supplierItemCode: string | null;
};

export function normCsvText(value: string | null | undefined): string {
  return (value ?? '').trim().replace(/\s+/g, ' ');
}

function normText(value: string | null | undefined): string {
  return normCsvText(value);
}

function moneyEqual(a: number, b: number): boolean {
  return Math.round(a * 100) === Math.round(b * 100);
}

export type CsvBarcodeParseResult = {
  barcode: string | null;
  error?: string;
  /** Valor inválido en actualización: el commit no toca el barcode guardado. */
  invalidIgnoredOnUpdate?: boolean;
};

/** Misma regla que commit/preview de import CSV. */
export function parseBarcodeForCsvCell(
  raw: string | undefined,
  opts: { isUpdate: boolean }
): CsvBarcodeParseResult {
  const trimmed = sanitizeBarcodeCsvCell(raw);
  if (!trimmed) return { barcode: null };
  if (opts.isUpdate && /[eE]/.test(trimmed)) {
    return { barcode: null, invalidIgnoredOnUpdate: true };
  }
  const barcodeRaw = normalizeCsvNumericFieldFromExcel(trimmed);
  if (!barcodeRaw) return { barcode: null };
  const normalized = normalizeProductCode(barcodeRaw);
  const bcErr = validateBarcode(normalized);
  if (bcErr) {
    if (opts.isUpdate) return { barcode: null, invalidIgnoredOnUpdate: true };
    return { barcode: null, error: bcErr };
  }
  return { barcode: normalized };
}

function effectiveBarcodeAfterCsv(
  raw: string | undefined,
  existingBarcode: string | null
): string | null {
  const parsed = parseBarcodeForCsvCell(raw, { isUpdate: true });
  return parsed.barcode != null ? parsed.barcode : existingBarcode;
}

function resolveCategoryId(
  record: ParsedRow,
  candidates: MasterMatchCandidate[],
  fallback: string | null
): string | null {
  if (!record.categoria_id?.trim() && !record.categoria?.trim()) return fallback;
  const match = resolveMasterFromRecordFields(candidates, {
    id: record.categoria_id,
    label: record.categoria,
  });
  return match.id ?? fallback;
}

function resolveBrandId(
  record: ParsedRow,
  candidates: MasterMatchCandidate[],
  fallback: string | null
): string | null {
  if (!record.marca_id?.trim() && !record.marca?.trim()) return fallback;
  const match = resolveMasterFromRecordFields(candidates, {
    id: record.marca_id,
    label: record.marca,
  });
  return match.id ?? fallback;
}

function resolveSupplierId(
  record: ParsedRow,
  candidates: MasterMatchCandidate[],
  fallback: string | null
): string | null {
  if (!record.proveedor_preferido_id?.trim() && !record.proveedor_preferido?.trim()) {
    return fallback;
  }
  const match = resolveMasterFromRecordFields(candidates, {
    id: record.proveedor_preferido_id,
    label: record.proveedor_preferido,
  });
  return match.id ?? fallback;
}

/** Motivos cortos para la vista previa (una sola línea). */
export function productCsvRowChangeReasons(
  existing: ProductCsvExistingSnapshot,
  record: ParsedRow,
  ctx: {
    categoryCandidates: MasterMatchCandidate[];
    brandCandidates: MasterMatchCandidate[];
    supplierCandidates: MasterMatchCandidate[];
  }
): string[] {
  const reasons: string[] = [];

  const nombreRaw = record.nombre?.trim();
  if (nombreRaw && normText(nombreRaw) !== normText(existing.name)) reasons.push('Nombre');

  const precioRaw = record.precio?.trim();
  if (precioRaw !== '' && precioRaw != null) {
    const precio = parseDecimalInput(precioRaw) ?? NaN;
    if (!Number.isNaN(precio) && precio > 0 && !moneyEqual(precio, existing.price)) reasons.push('Precio');
  }

  const nextBarcode = effectiveBarcodeAfterCsv(record.codigo_barras, existing.barcode);
  if (normText(nextBarcode) !== normText(existing.barcode)) reasons.push('Código barras');

  const itemType = parseCsvItemType(record.tipo) ?? existing.itemType;
  if (itemType !== existing.itemType) reasons.push('Tipo');

  const categoryId = resolveCategoryId(record, ctx.categoryCandidates, existing.categoryId);
  if (
    !masterCandidateIdsEqual(categoryId, existing.categoryId, ctx.categoryCandidates)
  ) {
    reasons.push('Categoría');
  }

  const brandId = resolveBrandId(record, ctx.brandCandidates, existing.brandId);
  if (!masterCandidateIdsEqual(brandId, existing.brandId, ctx.brandCandidates)) {
    reasons.push('Marca');
  }

  const preferredSupplierId = resolveSupplierId(
    record,
    ctx.supplierCandidates,
    existing.preferredSupplierId
  );
  if (
    !masterCandidateIdsEqual(
      preferredSupplierId,
      existing.preferredSupplierId,
      ctx.supplierCandidates
    )
  ) {
    reasons.push('Proveedor');
  }

  if (record.notas?.trim() !== '' && record.notas != null) {
    if (record.notas.trim() !== normText(existing.internalNotes)) reasons.push('Notas');
  }

  if (record.codigo_proveedor?.trim() !== '' && record.codigo_proveedor != null) {
    if (record.codigo_proveedor.trim() !== normText(existing.supplierItemCode)) {
      reasons.push('Cód. producto proveedor');
    }
  }

  const activeFlag = parseCsvBoolean(record.activo);
  if (activeFlag !== undefined) {
    const currentActive = existing.isActive !== 0;
    if (activeFlag !== currentActive) reasons.push('Activo');
  }

  return reasons;
}

/**
 * True si commitProductRow escribiría algo distinto al producto existente.
 * Vacío en CSV = no tocar ese campo (misma regla que el commit).
 */
export function productCsvRowWouldChangeOnUpdate(
  existing: ProductCsvExistingSnapshot,
  record: ParsedRow,
  ctx: {
    categoryCandidates: MasterMatchCandidate[];
    brandCandidates: MasterMatchCandidate[];
    supplierCandidates: MasterMatchCandidate[];
  }
): boolean {
  return productCsvRowChangeReasons(existing, record, ctx).length > 0;
}

/** True si el commit no modificaría ningún campo (misma resolución que `commitProductRow`). */
export function productCsvExistingMatchesResolvedCommit(
  existing: ProductCsvExistingSnapshot,
  resolved: {
    name: string;
    price: number;
    sku: string;
    categoryId: string | null;
    brandId: string | null;
    preferredSupplierId: string | null;
    itemType: string;
    barcode: string | null;
    internalNotes: string | null;
    supplierItemCode: string | null;
    activeFlag?: boolean;
  },
  ctx: {
    categoryCandidates: MasterMatchCandidate[];
    brandCandidates: MasterMatchCandidate[];
    supplierCandidates: MasterMatchCandidate[];
  }
): boolean {
  if (normCsvText(resolved.name) !== normCsvText(existing.name)) return false;
  if (!moneyEqual(resolved.price, existing.price)) return false;
  if (normalizeProductCode(resolved.sku) !== normalizeProductCode(existing.sku ?? '')) return false;
  if (resolved.itemType !== existing.itemType) return false;
  if (normText(resolved.barcode) !== normText(existing.barcode)) return false;
  if (normText(resolved.internalNotes) !== normText(existing.internalNotes)) return false;
  if (normText(resolved.supplierItemCode) !== normText(existing.supplierItemCode)) return false;
  if (
    !masterCandidateIdsEqual(
      resolved.categoryId,
      existing.categoryId,
      ctx.categoryCandidates
    )
  ) {
    return false;
  }
  if (!masterCandidateIdsEqual(resolved.brandId, existing.brandId, ctx.brandCandidates)) {
    return false;
  }
  if (
    !masterCandidateIdsEqual(
      resolved.preferredSupplierId,
      existing.preferredSupplierId,
      ctx.supplierCandidates
    )
  ) {
    return false;
  }
  if (resolved.activeFlag !== undefined && resolved.activeFlag !== (existing.isActive !== 0)) {
    return false;
  }
  return true;
}
